import { declareParams, JsonApiRequestError } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { applyTimeZone, RegistryParams, RegistryQuery } from 'shared/lib';
import { createAuthQuery } from '../auth-http-client';
import { createPaginatedSchema, ShiftReport, shiftReportSchema } from '../types';

dayjs.extend(utc);

export type OriginalShiftReportRegistryParams = {
	sortField?: string | null;
	sortDirection?: string;
	page?: number;
	limit?: number;
	currencies?: string[];
	accountIds?: string[];
	cashierIds?: string[];
	cashdeskIds?: string[];
	dateFrom?: string;
	dateTo?: string;
	timeZone?: number;
};

export type ShiftReportRegistryFilters = {
	currencies: string[];
	accountIds: number[];
	cashierIds: number[];
	cashdeskIds: number[];
	dateFrom: string;
	dateTo: string;
	timeZone: number;
};

export type ShiftReportRegistryParams = RegistryParams<ShiftReportRegistryFilters>;

export type ShiftReportRegistryQuery = RegistryQuery<
	ShiftReport,
	ShiftReportRegistryFilters,
	JsonApiRequestError
>;

export const createShiftReportRegistryQuery = (): ShiftReportRegistryQuery => {
	const query = createAuthQuery({
		params: declareParams<ShiftReportRegistryParams>(),
		request: {
			method: 'GET',
			url: '/report/shift',
			query: (params) => ({
				page: params.page,
				limit: params.limit,
				sortField: params.sortField ?? undefined,
				sortDirection: params.sortDirection,
				currencies: params.filters.currencies?.length === 0 ? undefined : params.filters.currencies,
				accountIds: [...params.filters.accountIds.map(String)],
				cashierIds: [...params.filters.cashierIds.map(String)],
				cashdeskIds: [...params.filters.cashdeskIds.map(String)],
				dateFrom: params.filters.dateFrom,
				dateTo: params.filters.dateTo,
				timeZone: params.filters.timeZone,
			}),
		},
		response: {
			contract: zodContract(createPaginatedSchema(shiftReportSchema)),
			mapData: ({ result, params }) => ({
				...result,
				rows: result.rows.map<ShiftReport>((row) => ({
					...row,
					startTime: applyTimeZone(row.startTime, params.filters.timeZone),
					endTime: row.endTime && applyTimeZone(row.endTime, params.filters.timeZone),
				})),
			}),
		},
	});

	return query;
};
